/**
 * mdb 匯入工具 schema(REQ-20260520):
 * 從 Microsoft Access (.mdb) 一次性匯入 ERP 主檔資料(Phase 1A 4 個 reference table)。
 * 後端 spawn mdbtools 解析,upsert by code(已存在的 code 跳過,沒有的 insert)。
 */
import { z } from 'zod';

/** mdb 內已知的舊系統表名(其他表會被忽略) */
export const MDB_KNOWN_TABLES = {
  PRODUCT_CATEGORY: '貨品類別',
  PRODUCT_UNIT: '貨品單位',
  CUSTOMER_SUPPLIER_CATEGORY: '客廠類別',
  WAREHOUSE: '倉庫資料',
  CUSTOMER_SUPPLIER: '客戶廠商',
  PRODUCT: '貨品主檔',
  PRODUCT_STOCK: '貨品庫存',
  // REQ-20260521 Phase 3A:AR/AP
  ACCOUNT_HEADER: '帳款主檔',
  SETTLEMENT_HEADER: '收付主檔',
  SETTLEMENT_LINE: '收付明細',
  // REQ-20260521 Phase 2D:出貨 / 進貨 / 庫存調整
  TRANSACTION_HEADER: '交易主檔',
  TRANSACTION_LINE: '交易明細',
} as const;

/** 目標(新系統)實體名稱 */
export const MDB_IMPORT_TARGETS = [
  'product-categories',
  'product-units',
  'supplier-categories',
  'warehouses',
  // REQ-20260520 ERP Phase 1D
  'suppliers',
  'products',
  // REQ-20260520 ERP Phase 2A
  'product-stocks',
  // REQ-20260521 ERP Phase 3A — AR/AP
  'customer-opening',
  'supplier-opening',
  'ar-history',
  'ap-history',
  // REQ-20260521 ERP Phase 2D — 出貨 / 進貨 / 庫存調整
  'sales-orders',
  'purchase-orders',
  'stock-adjustments',
] as const;
export const MdbImportTargetSchema = z.enum(MDB_IMPORT_TARGETS);
export type MdbImportTarget = z.infer<typeof MdbImportTargetSchema>;

/** 預覽:每張舊表的筆數 + 目前 DB 對應 entity 的筆數 */
export const MdbImportTableSummarySchema = z.object({
  /** 對應新系統的 entity path,例 'product-categories' */
  target: MdbImportTargetSchema,
  /** 新系統 nav label(中文) */
  targetLabel: z.string(),
  /** 舊 mdb 表名 */
  sourceTable: z.string(),
  /** 舊 mdb 該表的列數 */
  sourceRowCount: z.number().int().nonnegative(),
  /** 若舊表需 filter(例如客廠類別要 filter 廠商),預估匯入幾筆 */
  importableCount: z.number().int().nonnegative(),
  /** 目前新系統 entity 已存在的筆數 */
  existingCount: z.number().int().nonnegative(),
  /**
   * REQ-20260521 Phase 3A:對 ar-history / customer-opening 等需 name 比對的 target,
   * 顯示「對得上 new customer 但會被處理的筆數」之外,還有多少 unique 舊對象比對不到。
   * 其他 target 不會帶這欄。
   */
  unmatchedPartyCount: z.number().int().nonnegative().optional(),
  /** 未匹配對象的前 N 筆 sample,讓 user 知道是哪幾家公司 */
  unmatchedSample: z
    .array(
      z.object({
        legacyCode: z.string(),
        fullName: z.string(),
        simpleName: z.string(),
      }),
    )
    .optional(),
});
export type MdbImportTableSummary = z.infer<typeof MdbImportTableSummarySchema>;

export const MdbImportPreviewResultSchema = z.object({
  /** mdb 大小(bytes) */
  fileSize: z.number().int().nonnegative(),
  /** mdb 內偵測到的所有表(僅前 N 個,給除錯用) */
  allTables: z.array(z.string()),
  /** 系統會處理的目標 → 對應狀態 */
  summaries: z.array(MdbImportTableSummarySchema),
});
export type MdbImportPreviewResult = z.infer<typeof MdbImportPreviewResultSchema>;

/** 執行匯入時前端送過來的 targets 清單(form field) */
export const MdbImportRunInputSchema = z.object({
  targets: z.array(MdbImportTargetSchema).min(1, '至少要勾選一個項目'),
});
export type MdbImportRunInput = z.infer<typeof MdbImportRunInputSchema>;

/** 單一 target 匯入的結果 */
export const MdbImportTargetResultSchema = z.object({
  target: MdbImportTargetSchema,
  targetLabel: z.string(),
  attempted: z.number().int().nonnegative(),
  inserted: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  /** 個別錯誤訊息(最多 10 條) */
  errors: z.array(z.string()).max(10),
});
export type MdbImportTargetResult = z.infer<typeof MdbImportTargetResultSchema>;

export const MdbImportRunResultSchema = z.object({
  /** 本次匯入紀錄 id(可用來查 history) */
  runId: z.number().int().optional(),
  results: z.array(MdbImportTargetResultSchema),
});
export type MdbImportRunResult = z.infer<typeof MdbImportRunResultSchema>;

// ─── Phase 1D.2:匯入歷史 ─────────────────────────────────────────────

/** 一筆 target 在歷史紀錄中的明細(含原始 source row count) */
export const MdbImportRunDetailItemSchema = z.object({
  target: z.string(),
  targetLabel: z.string(),
  sourceTable: z.string(),
  sourceRowCount: z.number().int().nonnegative(),
  importableCount: z.number().int().nonnegative(),
  attempted: z.number().int().nonnegative(),
  inserted: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  errors: z.array(z.string()),
});
export type MdbImportRunDetailItem = z.infer<typeof MdbImportRunDetailItemSchema>;

export const MdbImportRunRowSchema = z.object({
  id: z.number().int(),
  uuid: z.string(),
  filename: z.string(),
  fileSize: z.number().int().nonnegative(),
  runById: z.number().int().nullable(),
  runByName: z.string().nullable(),
  startedAt: z.string().datetime(),
  finishedAt: z.string().datetime().nullable(),
  status: z.enum(['ok', 'partial', 'failed']),
  totalAttempted: z.number().int().nonnegative(),
  totalInserted: z.number().int().nonnegative(),
  totalSkipped: z.number().int().nonnegative(),
  totalFailed: z.number().int().nonnegative(),
  details: z.array(MdbImportRunDetailItemSchema),
});
export type MdbImportRunRow = z.infer<typeof MdbImportRunRowSchema>;

export const MdbImportHistoryResponseSchema = z.object({
  items: z.array(MdbImportRunRowSchema),
});
export type MdbImportHistoryResponse = z.infer<typeof MdbImportHistoryResponseSchema>;
